export interface IDeferred<T = any> {
    /**
     * Promise 对象，await 时总是返回最新值
     * 如果已经有值则立即 resolve，否则等待下次 resolve
     */
    promise: Promise<T>

    /**
     * 设置/更新值（可多次调用）
     */
    resolve: (value: T) => void

    /**
     * 拒绝 Promise
     */
    reject: (reason?: any) => void

    /**
     * 获取当前值（同步）
     * @returns 当前值，如果还没有 resolve 则返回 undefined
     */
    getValue: () => T | undefined

    /**
     * 监听值变化
     * @param callback 值变化时的回调函数
     * @returns 取消订阅的函数
     */
    onChange: (callback: (value: T) => void) => () => void

    /**
     * 是否已经有值
     */
    hasValue: boolean

    /**
     * Deferred 的标识名称（用于调试）
     */
    name?: string
}

export function create<T = any>(name?: string): IDeferred<T> {
    let currentValue: T | undefined = undefined;
    let hasValue = false;
    const listeners = new Set<(value: T) => void>();

    // 当前等待中的 resolve/reject 回调
    let pendingResolve: ((value: T) => void) | null = null;
    let pendingReject: ((reason?: any) => void) | null = null;

    const deferredName = name || 'unnamed';

    const deferred: IDeferred<T> = {
        get promise() {
            // 每次访问 promise 时都创建新的
            // 如果已有值则立即 resolve，否则等待
            if (hasValue) {
                return Promise.resolve(currentValue as T);
            } else {
                return new Promise<T>((resolve, reject) => {
                    pendingResolve = resolve;
                    pendingReject = reject;
                });
            }
        },

        resolve(value: T) {
            currentValue = value;
            hasValue = true;

            // 如果有等待中的 promise，resolve 它
            if (pendingResolve) {
                pendingResolve(value);
                pendingResolve = null;
                pendingReject = null;
            }

            // 通知所有监听器
            listeners.forEach((listener) => {
                try {
                    listener(value);
                } catch (error) {
                    console.error(`[Deferred:${deferredName}] Error in onChange listener:`, error);
                }
            });
        },

        reject(reason?: any) {
            if (pendingReject) {
                pendingReject(reason);
                pendingResolve = null;
                pendingReject = null;
            }
        },

        getValue() {
            return currentValue;
        },

        onChange(callback: (value: T) => void) {
            listeners.add(callback);

            // 如果已经有值，立即调用一次
            if (hasValue && currentValue !== undefined) {
                try {
                    callback(currentValue);
                } catch (error) {
                    console.error(`[Deferred:${deferredName}] Error in onChange callback:`, error);
                }
            }

            // 返回取消订阅函数
            return () => {
                listeners.delete(callback);
            };
        },

        get hasValue() {
            return hasValue;
        },

        name: deferredName,
    };

    return deferred;
}
